import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { AbsenceType } from "../../domain/AbsenceType";
import { CarnetType } from "../../domain/CarnetType";
import { ICarnet } from "../../domain/ICarnet";
import { ICarnetCreate } from "../../domain/ICarnetCreate";
import { ICarnetEmployee } from "../../domain/ICarnetEmployee";
import { ICarnetHoursUpdate } from "../../domain/ICarnetHoursUpdate";
import { ICarnetProjectUpdate } from "../../domain/ICarnetProjectUpdate";
import { ICarentsTotals } from "../../domain/ICarnetsTotals";
import { ICheckboxValueUpdate } from "../../domain/ICheckboxValueUpdate";
import { IGetCarnetEmployeesRequestParams } from "../../domain/IGetCarnetEmployeesRequestParams";
import { IGetCarnetsRequestParams } from "../../domain/IGetCarnetsRequestParams";
import { MealType } from "../../domain/MealType";
import { carnetService } from "../../services/carnet.service";
import { ICreateCarnetsExtendedRequestParams } from "../../domain/ICreateCarnetsExtendedRequestParams";
import { IPopoverState } from "../../domain/IPopoverState";
import { IUpdateEmployeeRequestParams } from "../../domain/IUpdateEmployeeRequestParams";
import { ICarnetDateUpdate } from "../../domain/ICarnetDateUpdate";
import { IResponseCarnetPayrolls } from "../../domain/IResponseCarnetPayrolls";
import { ICarnetPayrollUpdate } from "../../domain/ICarnetPayrollUpdate";
import { ICarnetPayrollAddExpense } from "../../domain/ICarnetPayrollAddExpense";
import { ICarnetPayrollUpdateExpense } from "../../domain/ICarnetPayrollUpdateExpense";

export const getCarnetEmployees = createAsyncThunk(
  "Carnets/getCarnetEmployees",
  async (data: IGetCarnetEmployeesRequestParams) => {
    try {
      return await carnetService.getCarnetEmployees(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getCarnetsInPeriod = createAsyncThunk(
  "Carnets/getCarnetsInPeriod",
  async (data: IGetCarnetsRequestParams) => {
    try {
      return await carnetService.getCarnetsInPeriod(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const createCarnets = createAsyncThunk(
  "Carnets/createCarnets",
  async (data: ICreateCarnetsExtendedRequestParams, { rejectWithValue }) => {
    try {
      return await carnetService.createCarnets({
        carnets: data.carnets,
      });
    } catch (err: any) {
      return rejectWithValue(err.response);
    }
  }
);

export const updateEmployeeData = createAsyncThunk(
  "Carnets/updateEmployeeData",
  async (employeeData: IUpdateEmployeeRequestParams) => {
    try {
      return await carnetService.updateEmployee(employeeData);
    } catch (err) {
      setError(err);
    }
  }
);

export const getEmployeePayrolls = createAsyncThunk(
  "Carnets/getEmployeePayrolls",
  async (employeeId: number) => {
    try {
      return await carnetService.getPayrollEntries(employeeId);
    } catch (err) {
      setError(err);
    }
  }
);

export const updateEmployeePayroll = createAsyncThunk(
  "Carnets/updateEmployeePayroll",
  async (data: ICarnetPayrollUpdate) => {
    try {
      return await carnetService.updateEmployeePayroll(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const addExpenseToPayroll = createAsyncThunk(
  "Carnets/addExpenseToPayroll",
  async (data: ICarnetPayrollAddExpense) => {
    try {
      if (data.amount !== null && data.amount !== undefined) {
        return await carnetService.addExpenseToPayroll(data);
      } else {
        return undefined;
      }
    } catch (err) {
      setError(err);
    }
  }
);

export const updateExpenseInPayroll = createAsyncThunk(
  "Carnets/updateExpenseInPayroll",
  async (data: ICarnetPayrollUpdateExpense) => {
    try {
      return await carnetService.updateExpenseInPayroll(data);
    } catch (err) {
      setError(err);
    }
  }
);

interface mainState {
  carnetEmployees: ICarnetEmployee[];
  loading: boolean;
  carnetsLoading: boolean;
  carnets: ICarnet[];
  totals: ICarentsTotals;
  searchModalOpen: boolean;
  vacationPopover: IPopoverState;
  infoPopover: IPopoverState;
  payrollPopover: IPopoverState;
  selectedIndex: number;
  carnetsMap: { [key in number]: ICarnetCreate };
  carnetsDateMap: { [key in number]: ICarnetDateUpdate };
  selectedEmployeePayrolls?: IResponseCarnetPayrolls;
  error: any;
}

const initialState: mainState = {
  carnetEmployees: [],
  loading: false,
  carnetsLoading: false,
  carnets: [],
  totals: {
    hoursTotals: [],
    effectiveKilometers: 0,
    effectiveHours: 0,
    regularBreakfastsTotal: 0,
    regularLunchesTotal: 0,
    regularDinnersTotal: 0,
    cashBreakfastsTotal: 0,
    cashLunchesTotal: 0,
    cashDinnersTotal: 0,
    sickLeavesTotal: 0,
    vacationsTotal: 0,
  },
  searchModalOpen: false,
  vacationPopover: {
    isOpen: false,
    top: 0,
    left: 0
  },
  infoPopover: {
    isOpen: false,
    top: 0,
    left: 0
  },
  payrollPopover: {
    isOpen: false,
    top: 0,
    left: 0
  },
  selectedIndex: -1,
  carnetsMap: {},
  carnetsDateMap: {},
  selectedEmployeePayrolls: undefined,
  error: null,
};

export const mainSlice = createSlice({
  name: "carnets-main",
  initialState: initialState,
  reducers: {
    setSearchModalOpen: (state, action: PayloadAction<boolean>) => {
      state.searchModalOpen = action.payload;
    },
    setVacationPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.vacationPopover = action.payload;
    },
    setInfoPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.infoPopover = action.payload;
    },
    setPayrollPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.payrollPopover = action.payload;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setCarnetEmployees: (state, action: PayloadAction<ICarnetEmployee[]>) => {
      state.carnetEmployees = action.payload;
    },
    updateCarnetsHours: (state, action: PayloadAction<ICarnetHoursUpdate>) => {
      const option = action.payload;
      if (state.carnetsMap[option.employeeId]) {
        let newCarnetHours = state.carnetsMap[option.employeeId].carnetHours ?? [];
        newCarnetHours[option.rateIndex] = option.value;
        state.carnetsMap[option.employeeId].carnetHours = newCarnetHours;

        if (option.value) {
          delete state.carnetsMap[option.employeeId].absenceType;
          state.carnetsMap[option.employeeId].carnetType = CarnetType.EMPLOYED;
          state.carnetsMap[option.employeeId].projectId = option.projectId;
          if (state.carnetsMap[option.employeeId].breakfast === undefined) {
            state.carnetsMap[option.employeeId].breakfast = MealType.NO_MEAL;
          }
          if (state.carnetsMap[option.employeeId].lunch === undefined) {
            state.carnetsMap[option.employeeId].lunch = MealType.NO_MEAL;
          }
          if (state.carnetsMap[option.employeeId].dinner === undefined) {
            state.carnetsMap[option.employeeId].dinner = MealType.NO_MEAL;
          }
        } else {
          //deleting value
          const { carnetHours, breakfast, lunch, dinner } =
            state.carnetsMap[option.employeeId];
          const condition1 =
            (breakfast === undefined || breakfast === MealType.NO_MEAL) &&
            (lunch === undefined || lunch === MealType.NO_MEAL) &&
            (dinner === undefined || dinner === MealType.NO_MEAL);
          const condition2 = carnetHours === undefined
            || carnetHours.every((hoursEntry, index) => hoursEntry === undefined || index === option.rateIndex);
          if (condition1 && condition2) {
            delete state.carnetsMap[option.employeeId];
          }
        }
      } else {
        if (option.value !== undefined && option.value !== null) {
          let carnetHours: (number | undefined)[] = [];
          carnetHours[option.rateIndex] = option.value;
          state.carnetsMap[option.employeeId] = {
            employeeId: option.employeeId,
            projectId: option.projectId,
            carnetHours: carnetHours,
            breakfast: MealType.NO_MEAL,
            lunch: MealType.NO_MEAL,
            dinner: MealType.NO_MEAL,
            carnetType: CarnetType.EMPLOYED,
          };
        }
      }
    },
    updateCarnetsDate: (state, action: PayloadAction<ICarnetDateUpdate>) => {
      const option = action.payload;
      if (state.carnetsDateMap[option.employeeId]) {
        state.carnetsDateMap[option.employeeId].carnetDate = option.carnetDate;
      } else {
        state.carnetsDateMap[option.employeeId] = {
          employeeId: option.employeeId,
          carnetDate: option.carnetDate
        };
      }
    },
    updateCarnetsProject: (
      state,
      action: PayloadAction<ICarnetProjectUpdate>
    ) => {
      const option = action.payload;
      if (state.carnetsMap[option.employeeId]) {
        state.carnetsMap[option.employeeId].projectId = option.projectId;
      }
      for (let employeeIndex = 0; employeeIndex < state.carnetEmployees.length; employeeIndex++) {
        if (state.carnetEmployees[employeeIndex].employeeId === option.employeeId) {
          state.carnetEmployees[employeeIndex].latestProjectId = option.projectId;
        }
      }
    },
    updateCarnetsCheckbox: (
      state,
      action: PayloadAction<ICheckboxValueUpdate>
    ) => {
      const option = action.payload;
      if (state.carnetsMap[option.employeeId]) {
        switch (option.name) {
          case "BREAKFAST_REGULAR":
          case "BREAKFAST_CASH":
          case "LUNCH_REGULAR":
          case "LUNCH_CASH":
          case "DINNER_REGULAR":
          case "DINNER_CASH": {
            if (option.checked) {
              if (option.name.includes("BREAKFAST")) {
                state.carnetsMap[option.employeeId].breakfast =
                  option.name === "BREAKFAST_REGULAR"
                    ? MealType.REGULAR
                    : MealType.CASH;
              } else if (option.name.includes("LUNCH")) {
                state.carnetsMap[option.employeeId].lunch =
                  option.name === "LUNCH_REGULAR"
                    ? MealType.REGULAR
                    : MealType.CASH;
              } else {
                state.carnetsMap[option.employeeId].dinner =
                  option.name === "DINNER_REGULAR"
                    ? MealType.REGULAR
                    : MealType.CASH;
              }
              state.carnetsMap[option.employeeId].carnetType =
                CarnetType.EMPLOYED;
              delete state.carnetsMap[option.employeeId].absenceType;
            } else {
              const { carnetHours, breakfast, lunch, dinner } =
                state.carnetsMap[option.employeeId];
              const condition1 = carnetHours === undefined || carnetHours.every(hoursEntry => hoursEntry === undefined);
              const condition2 = option.name.includes("BREAKFAST")
                ? (lunch === undefined || lunch === MealType.NO_MEAL) && (dinner === undefined || dinner === MealType.NO_MEAL)
                : option.name.includes("LUNCH")
                  ? (breakfast === undefined || breakfast === MealType.NO_MEAL) && (dinner === undefined || dinner === MealType.NO_MEAL)
                  : (breakfast === undefined || breakfast === MealType.NO_MEAL) && (lunch === undefined || lunch === MealType.NO_MEAL);
              if (condition1 && condition2) {
                delete state.carnetsMap[option.employeeId];
              } else {
                if (option.name.includes("BREAKFAST")) {
                  state.carnetsMap[option.employeeId].breakfast = MealType.NO_MEAL;
                } else if (option.name.includes("LUNCH")) {
                  state.carnetsMap[option.employeeId].lunch = MealType.NO_MEAL;
                } else {
                  state.carnetsMap[option.employeeId].dinner = MealType.NO_MEAL;
                }
              }
            }
            break;
          }

          case "SICK_LEAVE": {
            if (option.checked) {
              state.carnetsMap[option.employeeId] = {
                employeeId: option.employeeId,
                carnetType: CarnetType.ABSENCE,
                absenceType: AbsenceType.SICK_LEAVE,
              };
            } else {
              delete state.carnetsMap[option.employeeId];
            }
            break;
          }
        }
      } else {
        switch (option.name) {
          case "BREAKFAST_REGULAR":
          case "BREAKFAST_CASH":
          case "LUNCH_REGULAR":
          case "LUNCH_CASH":
          case "DINNER_REGULAR":
          case "DINNER_CASH": {
            if (option.checked) {
              state.carnetsMap[option.employeeId] = {
                employeeId: option.employeeId,
                carnetType: CarnetType.EMPLOYED,
              };
              if (option.name.includes("BREAKFAST")) {
                state.carnetsMap[option.employeeId].breakfast =
                  option.name === "BREAKFAST_REGULAR"
                    ? MealType.REGULAR
                    : MealType.CASH;
                state.carnetsMap[option.employeeId].lunch = MealType.NO_MEAL;
                state.carnetsMap[option.employeeId].dinner = MealType.NO_MEAL;
              } else if (option.name.includes("LUNCH")) {
                state.carnetsMap[option.employeeId].lunch =
                  option.name === "LUNCH_REGULAR"
                    ? MealType.REGULAR
                    : MealType.CASH;
                state.carnetsMap[option.employeeId].breakfast = MealType.NO_MEAL;
                state.carnetsMap[option.employeeId].dinner = MealType.NO_MEAL;
              } else {
                state.carnetsMap[option.employeeId].dinner =
                  option.name === "DINNER_REGULAR"
                    ? MealType.REGULAR
                    : MealType.CASH;
                state.carnetsMap[option.employeeId].breakfast = MealType.NO_MEAL;
                state.carnetsMap[option.employeeId].lunch = MealType.NO_MEAL;
              }
            }
            break;
          }
          case "SICK_LEAVE": {
            if (option.checked) {
              state.carnetsMap[option.employeeId] = {
                employeeId: option.employeeId,
                carnetType: CarnetType.ABSENCE,
                absenceType: AbsenceType.SICK_LEAVE,
              };
            } else {
              delete state.carnetsMap[option.employeeId];
            }
            break;
          }
          default: {
            break;
          }
        }
      }
    },
    clearCarnetsMap: (state) => {
      state.carnetsMap = {};
    },

    setSelectedIndex: (state, action: PayloadAction<number>) => {
      state.selectedIndex = action.payload;
    },
    updateEmployeeTemporarily: (state, action: PayloadAction<IUpdateEmployeeRequestParams>) => {
      if (action.payload) {
        for (let employeeIndex = 0; employeeIndex < state.carnetEmployees.length; employeeIndex++) {
          if (state.carnetEmployees[employeeIndex].employeeId === action.payload.employeeId) {
            if (action.payload.employedUntil !== undefined) {
              state.carnetEmployees[employeeIndex].employedUntil = action.payload.employedUntil;
            }
            if (action.payload.healthInsuranceUntil !== undefined) {
              state.carnetEmployees[employeeIndex].healthInsuranceUntil = action.payload.healthInsuranceUntil;
            }
            if (action.payload.birthDate !== undefined) {
              state.carnetEmployees[employeeIndex].birthDate = action.payload.birthDate;
            }
            if (action.payload.governmentId !== undefined) {
              state.carnetEmployees[employeeIndex].governmentId = action.payload.governmentId;
            }
            if (action.payload.idIssuedBy !== undefined) {
              state.carnetEmployees[employeeIndex].idIssuedBy = action.payload.idIssuedBy;
            }
            if (action.payload.address !== undefined) {
              state.carnetEmployees[employeeIndex].address = action.payload.address;
            }
            if (action.payload.phone !== undefined) {
              state.carnetEmployees[employeeIndex].phone = action.payload.phone;
            }
            if (action.payload.notes !== undefined) {
              state.carnetEmployees[employeeIndex].notes = action.payload.notes;
            }
          }
        }
      }
    },
    updateCarnetPayrollTemporarily: (state, action: PayloadAction<ICarnetPayrollUpdate>) => {
      if (action.payload && state.selectedEmployeePayrolls) {
        for (let payrollIndex = 0; payrollIndex < state.selectedEmployeePayrolls.payrolls.length; payrollIndex++) {
          if (state.selectedEmployeePayrolls.payrolls[payrollIndex].payrollId === action.payload.payrollId) {
            if (action.payload.bonus !== undefined) {
              state.selectedEmployeePayrolls.payrolls[payrollIndex].bonus = action.payload.bonus;
            }
            if (action.payload.paidByBank !== undefined) {
              state.selectedEmployeePayrolls.payrolls[payrollIndex].paidByBank = action.payload.paidByBank;
            }
            if (action.payload.travelExpenses !== undefined) {
              state.selectedEmployeePayrolls.payrolls[payrollIndex].travelExpenses = action.payload.travelExpenses;
            }
            if (action.payload.note !== undefined) {
              state.selectedEmployeePayrolls.payrolls[payrollIndex].note = action.payload.note;
            }
          }
        }
      }
    },
    updateExpenseInCarnetPayrollTemporarily: (state, action: PayloadAction<ICarnetPayrollUpdateExpense>) => {
      if (action.payload && state.selectedEmployeePayrolls) {
        for (let payrollIndex = 0; payrollIndex < state.selectedEmployeePayrolls.payrolls.length; payrollIndex++) {
          if (state.selectedEmployeePayrolls.payrolls[payrollIndex].payrollId === action.payload.payrollId) {
            for (let expenseIndex = 0; expenseIndex < state.selectedEmployeePayrolls.payrolls[payrollIndex].expenses.length; expenseIndex++) {
              if (state.selectedEmployeePayrolls.payrolls[payrollIndex].expenses[expenseIndex].expenseId === action.payload.expenseId) {
                if (action.payload.amount !== undefined) {
                  state.selectedEmployeePayrolls.payrolls[payrollIndex].expenses[expenseIndex].amount = action.payload.amount;
                }
                if (action.payload.note !== undefined) {
                  state.selectedEmployeePayrolls.payrolls[payrollIndex].expenses[expenseIndex].note = action.payload.note;
                }
              }
            }
          }
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getCarnetEmployees.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getCarnetEmployees.fulfilled, (state, action) => {
      state.carnetEmployees = action.payload ? action.payload : [];
      state.loading = false;
    });
    builder.addCase(getCarnetEmployees.rejected, (state, action) => {
      state.error = action.error;
      state.loading = false;
    });
    builder.addCase(updateEmployeeData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateEmployeeData.fulfilled, (state, action) => {
      if (action.payload) {
        let newCarnetEmployees: ICarnetEmployee[] = [];
        for (let employeeIndex = 0; employeeIndex < state.carnetEmployees.length; employeeIndex++) {
          const currentEmployee = state.carnetEmployees[employeeIndex];
          if (currentEmployee.employeeId === action.payload.employeeId) {
            newCarnetEmployees.push(action.payload);
          } else {
            newCarnetEmployees.push(currentEmployee);
          }
        }
        state.carnetEmployees = newCarnetEmployees;
      }
      state.loading = false;
    });
    builder.addCase(updateEmployeeData.rejected, (state, action) => {
      state.error = action.error;
      state.loading = false;
    });
    builder.addCase(getCarnetsInPeriod.pending, (state) => {
      state.carnetsLoading = true;
    });
    builder.addCase(getCarnetsInPeriod.fulfilled, (state, action) => {
      state.carnets = action.payload ? action.payload.carnets : [];
      state.totals.hoursTotals = action.payload
        ? action.payload.hoursTotals
        : [];
      state.totals.effectiveKilometers = action.payload
        ? action.payload.effectiveKilometers
        : 0;
      state.totals.effectiveHours = action.payload
        ? action.payload.effectiveHours
        : 0;
      state.totals.regularBreakfastsTotal = action.payload
        ? action.payload.regularBreakfastsTotal
        : 0;
      state.totals.regularLunchesTotal = action.payload
        ? action.payload.regularLunchesTotal
        : 0;
      state.totals.regularDinnersTotal = action.payload
        ? action.payload.regularDinnersTotal
        : 0;
      state.totals.cashBreakfastsTotal = action.payload
        ? action.payload.cashBreakfastsTotal
        : 0;
      state.totals.cashLunchesTotal = action.payload
        ? action.payload.cashLunchesTotal
        : 0;
      state.totals.cashDinnersTotal = action.payload
        ? action.payload.cashDinnersTotal
        : 0;
      state.totals.sickLeavesTotal = action.payload
        ? action.payload.sickLeavesTotal
        : 0;
      state.totals.vacationsTotal = action.payload
        ? action.payload.vacationsTotal
        : 0;
      state.carnetsLoading = false;
    });
    builder.addCase(getCarnetsInPeriod.rejected, (state, action) => {
      state.error = action.error;
      state.carnetsLoading = false;
    });
    builder.addCase(createCarnets.pending, (state) => {
      state.carnetsLoading = true;
    });
    builder.addCase(createCarnets.fulfilled, (state) => {
      state.carnetsLoading = false;
    });
    builder.addCase(createCarnets.rejected, (state, actions) => {
      state.carnetsLoading = false;
      state.error = actions.error;
    });
    builder.addCase(getEmployeePayrolls.pending, (state) => {
      state.selectedEmployeePayrolls = undefined;
    });
    builder.addCase(getEmployeePayrolls.fulfilled, (state, action) => {
      state.selectedEmployeePayrolls = action.payload;
    });
    builder.addCase(getEmployeePayrolls.rejected, (state, actions) => {
      state.selectedEmployeePayrolls = undefined;
    });
    builder.addCase(updateEmployeePayroll.fulfilled, (state, action) => {
      if (state.selectedEmployeePayrolls !== undefined && action.payload !== undefined) {
        state.selectedEmployeePayrolls.payrolls = state.selectedEmployeePayrolls.payrolls.map(oldPayroll => {
          if (oldPayroll.payrollId === action.payload?.payrollId) {
            return action.payload;
          } else {
            return oldPayroll;
          }
        });
      }
    });
    builder.addCase(addExpenseToPayroll.fulfilled, (state, action) => {
      if (state.selectedEmployeePayrolls !== undefined && action.payload !== undefined) {
        state.selectedEmployeePayrolls.payrolls = state.selectedEmployeePayrolls.payrolls.map(oldPayroll => {
          if (oldPayroll.payrollId === action.payload?.payrollId) {
            return action.payload;
          } else {
            return oldPayroll;
          }
        });
      }
    });
    builder.addCase(updateExpenseInPayroll.fulfilled, (state, action) => {
      if (state.selectedEmployeePayrolls !== undefined && action.payload !== undefined) {
        state.selectedEmployeePayrolls.payrolls = state.selectedEmployeePayrolls.payrolls.map(oldPayroll => {
          if (oldPayroll.payrollId === action.payload?.payrollId) {
            return action.payload;
          } else {
            return oldPayroll;
          }
        });
      }
    });
  },
});

export const {
  setError,
  setCarnetEmployees,
  setSearchModalOpen,
  setVacationPopover,
  setPayrollPopover,
  updateCarnetsHours,
  updateCarnetsDate,
  updateCarnetsCheckbox,
  updateCarnetsProject,
  clearCarnetsMap,
  setSelectedIndex,
  setInfoPopover,
  updateEmployeeTemporarily,
  updateCarnetPayrollTemporarily,
  updateExpenseInCarnetPayrollTemporarily,
} = mainSlice.actions;

export const selectState = (state: RootState) => state.carnets.main;
export const selectEmployee = (state: RootState) =>
  state.carnets.main.carnetEmployees[state.carnets.main.selectedIndex];
export const selectCarnetHoursTotal = (state: RootState) => {
  if (Object.keys(state.carnets.main.carnetsMap).length === 0) {
    return [];
  }
  let carnetHoursTotals: number[] = [];
  for (const property in state.carnets.main.carnetsMap) {
    let carnetHours = state.carnets.main.carnetsMap[property].carnetHours;
    if (carnetHours !== undefined) {
      carnetHours.forEach((hourEntry, index) => {
        carnetHoursTotals[index] = (carnetHoursTotals[index] ?? 0) + (hourEntry ?? 0);
      });
    }
  }
  for (let index = 0; index < carnetHoursTotals.length; index++) {
    if (carnetHoursTotals[index] === undefined) {
      carnetHoursTotals[index] = 0;
    }
  }
  return carnetHoursTotals;
};

export default mainSlice.reducer;
